import config from "../../../config";
import { sendAuthenticatedAsyncRequest } from "../../../services/AsyncRequestService";
import { mapLinkPagesToApiPayload } from "components/UnprocessedFaxDetails/UnprocessedFaxLinkPageFields";

/**
 * Service for handling all API calls related to Unprocessed Fax Details
 */
class UnprocessedFaxDetailsService {
  /**
   * Fetch unprocessed fax details
   */
  async fetchUnprocessedFaxDetails(faxInfoId) {
    const uri = `/fax/unprocessed-fax-details?fax_info_id=${faxInfoId}`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to fetch fax details: ${statusText}`));
        }
      );
    });
  }

  /**
   * Link unprocessed fax pages to switch
   */
  async linkUnprocessedFaxToSwitch(
    faxId,
    linkPages,
    isFaxCompletelyLinked = false,
    faxProgram = "",
    bypassAutoDisposition = false
  ) {
    const transformedLinkPages = linkPages.map((page) => ({
      ...(faxProgram ? { faxProgram } : {}),
      faxReferenceId: page.identifier,
      isUpsideDown: page.isUpsideDown || false,
      pageIndex: page.referenceField ? parseInt(page.referenceField, 10) : null,
      overrideAssociatedSwitch: page.overrideAssociatedSwitch || false,
      additionalPages: page.additionalField || null,
    }));

    const uri = `/ufl/std/link-fax`;
    const body = {
      faxInfoId: faxId,
      noOfLinkPages: transformedLinkPages.length,
      linkPages: transformedLinkPages,
      isFaxCompletelyLinked: isFaxCompletelyLinked,
      bypassAutoDisposition: Boolean(bypassAutoDisposition),
    };

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "POST",
        body,
        () => {
          resolve();
        },
        (error) => {
          const message =
            error?.data?.message ||
            error?.statusText ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to link fax: ${message}`));
        }
      );
    });
  }

  /**
   * Discard fax
   */
  async discardFax(faxInfoId) {
    const uri = `/fax/discard-fax?fax_info_id=${faxInfoId}`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to discard fax: ${statusText}`));
        }
      );
    });
  }

  /**
   * Set priority of unprocessed fax
   */
  async setPriorityOfUnprocessedFax(faxId, priority) {
    const uri = `/fax/unprocessed-fax-details/${faxId}/priority`;
    const body = { priority: priority || "" };

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "PUT",
        body,
        () => {
          resolve({ success: true });
        },
        (error) => {
          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to update priority: ${statusText}`));
        }
      );
    });
  }

  /**
   * Fetch note for unlinked fax
   */
  async fetchNoteForUnlinkedFax(faxId) {
    const uri = `/fax/unprocessed-fax-details/${faxId}/get-note`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          // Preserve previous behavior: 404 -> null instead of throwing
          const status = error?.status;
          if (status === 404) {
            resolve(null);
            return;
          }

          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to fetch note: ${statusText}`));
        }
      );
    });
  }

  /**
   * Save note
   */
  async saveNote(faxId, noteObject) {
    const uri = `/fax/unprocessed-fax-details/${faxId}/fax-note`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "POST",
        noteObject,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          const message =
            error?.data?.message ||
            error?.statusText ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to save note: ${message}`));
        }
      );
    });
  }

  /**
   * Update note
   */
  async updateNote(faxId, noteObject) {
    const uri = `/fax/unprocessed-fax-details/${faxId}/fax-note`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "POST",
        noteObject,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          const message =
            error?.data?.message ||
            error?.statusText ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to update note: ${message}`));
        }
      );
    });
  }

  /**
   * Delete note
   */
  async deleteNote(noteId) {
    const uri = `/fax/unprocessed-fax-details/${noteId}/delete-note`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "PUT",
        null,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to delete note: ${statusText}`));
        }
      );
    });
  }

  /**
   * Fetch all available tags
   */
  async fetchAllTags() {
    const uri = `/api/fax-tags`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          const data = response.data || [];
          resolve(data.map((tag) => tag.faxTagId));
        },
        (error) => {
          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to fetch tags: ${statusText}`));
        }
      );
    });
  }

  /**
   * Fetch fax program codes (for Unknown fax type tags)
   */
  async fetchFaxProgramTags() {
    const uri = `/fax-program/codes`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          const data = response.data;
          resolve(Array.isArray(data) ? data : []);
        },
        (error) => {
          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to fetch fax program codes: ${statusText}`));
        }
      );
    });
  }

  /**
   * Fetch tags for a specific fax
   */
  async fetchFaxTags(faxInfoId) {
    const uri = `/api/faxes/${faxInfoId}/tags`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          // Preserve previous behavior: 404 -> [] instead of throwing
          const status = error?.status;
          if (status === 404) {
            resolve([]);
            return;
          }

          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to fetch fax tags: ${statusText}`));
        }
      );
    });
  }

  /**
   * Save tags for a fax
   */
  async saveTags(faxInfoId, tags) {
    const uri = `/api/faxes/${faxInfoId}/tags`;
    const body = { tags };

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "POST",
        body,
        () => {
          resolve({ success: true });
        },
        (error) => {
          const message =
            error?.data?.message ||
            error?.statusText ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to save tags: ${message}`));
        }
      );
    });
  }

  /**
   * Fetch existing linked NPIs/pages for a fax (for NON_SWITCH)
   */
  async fetchLinkedNpisForFax(faxInfoId) {
    const uri = `/fax-archive/fax-info/${faxInfoId}`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "GET",
        null,
        (response) => {
          resolve(response.data);
        },
        (error) => {
          // Preserve previous behavior: 404 -> [] instead of throwing
          const status = error?.status;
          if (status === 404) {
            resolve([]);
            return;
          }

          const statusText =
            error?.statusText ||
            error?.data?.message ||
            error?.message ||
            "Unknown error";
          reject(new Error(`Failed to fetch linked NPIs: ${statusText}`));
        }
      );
    });
  }

  /**
   * Complete linking page for NON_SWITCH fax
   */
  async completePageLinking(faxInfoId) {
    const uri = `/fax/complete-linkage/${faxInfoId}`;

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        uri,
        "PUT",
        null,
        () => {
          resolve();
        },
        (error) => {
          let errorMessage = "Failed to complete linking";
          if (error?.data?.message) {
            errorMessage = error.data.message;
          } else if (error?.statusText) {
            errorMessage = `Failed to complete linking: ${error.statusText}`;
          } else if (error?.message) {
            errorMessage = error.message;
          }
          reject(new Error(errorMessage));
        }
      );
    });
  }


  buildLinkSubmitRequestBody(
    faxInfoId,
    linkPages = [],
    isFaxCompletelyLinked = false,
    linkFieldsConfig = [],
    linkSubmitApi = {},
    bypassAutoDisposition = false
  ) {
    const {
      requestBodyKeys = {
        faxInfoId: "faxInfoId",
        isFaxCompletelyLinked: "isFaxCompletelyLinked",
        linkPages: "linkPages",
        bypassAutoDisposition: "bypassAutoDisposition",
      },
      supplementalLinkPagePayload = { isUpsideDown: "isUpsideDown" },
      linkPagePayloadKeys = { faxProgram: "faxProgram" },
      extraBody = {},
      faxProgram,
    } = linkSubmitApi;

    const mappedLinkPages = mapLinkPagesToApiPayload(linkPages, linkFieldsConfig, {
      supplementalLinkPagePayload,
      faxProgram,
      linkPagePayloadKeys,
    });

    return {
      [requestBodyKeys.faxInfoId]: faxInfoId,
      [requestBodyKeys.isFaxCompletelyLinked]: isFaxCompletelyLinked,
      [requestBodyKeys.linkPages]: mappedLinkPages,
      [requestBodyKeys.bypassAutoDisposition]: Boolean(bypassAutoDisposition),
      ...extraBody,
    };
  }

  async submitLinkFax(
    faxInfoId,
    linkPages,
    isFaxCompletelyLinked = false,
    linkFieldsConfig = [],
    linkSubmitApi = {},
    bypassAutoDisposition = false
  ) {
    const requestBody = this.buildLinkSubmitRequestBody(
      faxInfoId,
      linkPages,
      isFaxCompletelyLinked,
      linkFieldsConfig,
      linkSubmitApi,
      bypassAutoDisposition
    );
    const method = (linkSubmitApi.method || "POST").toUpperCase();
    const url = linkSubmitApi.url;

    if (!url) {
      return Promise.reject(new Error("Link submit API URL is not configured."));
    }

    const errorPrefix =
      linkSubmitApi.errorMessagePrefix || "Failed to submit link request";

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        url,
        method,
        requestBody,
        (response) => {
          resolve(response?.data || { success: true });
        },
        (error) => {
          const message =
            error?.data?.message ||
            error?.statusText ||
            error?.message ||
            "Unknown error";
          reject(new Error(`${errorPrefix}: ${message}`));
        }
      );
    });
  }

  /**
   * Link NPI to fax (NON_SWITCH STD workflow — /fax-archive).
   */
  async linkAndArchiveFax(
    faxInfoId,
    linkPages,
    isFaxCompletelyLinked = false,
    linkFieldsConfig = [],
    faxProgram = "",
    bypassAutoDisposition = false
  ) {
    return this.submitLinkFax(
      faxInfoId,
      linkPages,
      isFaxCompletelyLinked,
      linkFieldsConfig,
      {
        method: "POST",
        url: `/ufl/${faxProgram}/link-fax`,
        faxProgram,
        errorMessagePrefix: "Failed to link NPI to fax",
      },
      bypassAutoDisposition
    );
  }
}

export default new UnprocessedFaxDetailsService();
